<script lang="ts">
	import { beforeUpdate, onDestroy, onMount } from 'svelte';
	import { edit } from 'src/stores/ui/edit_lists';
	import { devices } from 'src/stores/lists/devices';
	import {
		groupMainFilter,
		resetGroupMainFilter,
		roomsGroupsFilter,
		getSelectedGroupFilterCount,
	} from 'src/stores/ui/filters';
	import { defaultSelectedRoom } from '../../../helpers/constants';
	import { ComponentType } from '@Shelly/shelly-design-system/helpers/enums';
	import { format } from '../../../services/i18n';
	import { load_image_from } from '../../../helpers/interface_helpers';
	import { requestReorderDevice, onStartDrag, onEndDrag } from '../list-helpers';
	import { commitStepReorder } from '../reorder_step_helpers';
	import type { ReorderStepDetail } from '../reorder_step_helpers';

	import { CardGrid, Toolbar, Breadcrumbs, IconButton, Input } from '@Shelly/shelly-design-system';

	import AddEditButtons from '../../AddEditButtons.svelte';
	import GroupsFilterModal from './GroupsFilterModal.svelte';

	import EmptyList from '../EmptyList.svelte';
	import ListEmptyStateSurface from '../ListEmptyStateSurface.svelte';

	import AddEditGroupWizard from '../../Groups/GroupWizard/AddEditGroupWizard.svelte';
	import RoomToolbar from '../Rooms/RoomToolbar.svelte';
	import RoomBreadCrumbs from '../Rooms/RoomBreadCrumbs.svelte';

	import type { Device } from '../../../types/Device';
	import { _deepClone, capFirstChar } from '../../../helpers/utilities';
	import { defaultGroupObject } from 'src/helpers/group_helpers';
	import type { Group } from 'src/types/Group';
	import { isSubAccount } from 'src/helpers/user_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import RoomToolbarWrapper from '../Rooms/RoomToolbarWrapper.svelte';
	import GroupsToolbar from './GroupsToolbar.svelte';
	import GroupsListContent from './GroupsListContent.svelte';
	import ReorderStepControls from '../ReorderStepControls.svelte';
	import ListSkeletonGrid from '../ListSkeletonGrid.svelte';
	import { forceListSkeleton } from 'src/stores/features/dev/force-list-skeleton';
	import GroupDeleteModal from './GroupDeleteModal.svelte';
	import { clearGroupsItemNewFlag } from 'src/helpers/add-entity-helpers';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';
	import {
		getSharedHomeEmptyListCopy,
		type SharedHomeManagedContext,
	} from 'src/helpers/houses/shared_home_messaging_helpers';

	export let list;
	export let type = 'groups';
	export let id;

	const flipDurationMs = 0;
	const requestType = 'group';

	let editGroup = _deepClone(defaultGroupObject) as Group;

	let showAddEditGroupModal = false;
	let showSharedManagedModal = false;
	let sharedManagedContext: SharedHomeManagedContext = 'add_group';
	let showDeleteGroupModal = false;
	let openFilterModal = false;

	let dragDisabled = true;
	let currentRoute = '';
	let currentRoomFilter = defaultSelectedRoom;
	// let open = null;
	$: isEmptyDeviceList =
		Object.values($devices).length === 0 ||
		Object.values($devices).filter((d: Device) => !d.shared).length === 0;
	$: isRoomFilter =
		type !== 'groups'
			? true
			: $roomsGroupsFilter.rooms.find((filter) => filter.value).value !==
				defaultSelectedRoom.value;

	$: notification =
		getSelectedGroupFilterCount($roomsGroupsFilter) > 0
			? ({
					title: getSelectedGroupFilterCount($roomsGroupsFilter),
					type: ComponentType.DANGER,
				} as const)
			: null;

	$: items = $edit && currentRoute === type && !isRoomFilterChanged() ? items : [...list];
	$: dndSettings = {
		items,
		flipDurationMs: flipDurationMs,
		dragDisabled,
	};

	onMount(() => {
		currentRoute = type;
	});

	onDestroy(() => {
		edit.set(false);
		resetGroupMainFilter();
		clearGroupsItemNewFlag(id);
	});

	beforeUpdate(() => {
		if (!$edit) handleEndDrag();

		if (currentRoute !== type) {
			currentRoute = type;
		}
	});

	const isRoomFilterChanged = () => {
		if (currentRoomFilter.value !== $roomsGroupsFilter.rooms.find((filter) => filter.value).value) {
			currentRoomFilter = {
				...$roomsGroupsFilter.rooms.find((filter) => filter.value),
			};
			return true;
		}

		return false;
	};

	const clearData = () => {
		editGroup = _deepClone(defaultGroupObject) as Group;
	};

	const onCardClick = (item) => {
		if ($edit) return;
		setSidePanelState({ type: 'group', id: item.id, open: true });
	};

	const onCardEdit = async (group) => {
		editGroup = { ...group };
		showAddEditGroupModal = true;
	};

	const onCardDelete = (group) => {
		editGroup = { ...group };
		showDeleteGroupModal = true;
	};

	const onFinishCardEdit = () => {
		setGroupsEditChanges();
		clearData();
	};

	const setGroupsEditChanges = () => {
		if ($edit) {
			items = items
				.map((item) => {
					if (item.id === editGroup.id) {
						return editGroup;
					}
					return item;
				})
				.filter((item) => {
					// Filter Rooms in edit mode to update data when group changes room
					if (
						$roomsGroupsFilter.rooms.find((filter) => filter.value).value !==
						defaultSelectedRoom.value
					) {
						return (
							item.room_id ===
							$roomsGroupsFilter.rooms.find((filter) => filter.value).value
						);
					}

					return item;
				});
		} else {
			items = [...items, editGroup];
		}
	};

	// DND Functions
	const handleStartDrag = () => {
		dragDisabled = false;
	};

	const handleEndDrag = () => {
		dragDisabled = true;
	};

	const handleReorder = (e: CustomEvent) => {
		items = e.detail.items;
	};

	const persistGroupPositions = async (reordered) => {
		const editedList = {};
		reordered.forEach((v, i) => {
			delete v?.isDndShadowItem;
			editedList[v.id] = {
				...v,
				position: i,
			};
		});

		if (Object.keys(editedList).length === 0) return;
		await requestReorderDevice(requestType, editedList);
	};

	const handleReorderEnd = async (e: CustomEvent) => {
		items = [...e.detail.items];
		handleEndDrag();
		await persistGroupPositions(items);
	};

	const handleStepReorder = async (e: CustomEvent<ReorderStepDetail>) => {
		const next = await commitStepReorder({
			items,
			index: e.detail.index,
			delta: e.detail.delta,
			cardEl: e.detail.cardEl,
			assign: (nextItems) => {
				items = nextItems;
			},
		});
		if (!next) return;
		await persistGroupPositions(next);
	};

	const handle_add_group = () => {
		if (isActiveHomeShared($homes.list)) {
			sharedManagedContext = 'add_group';
			showSharedManagedModal = true;
			return;
		}
		edit.set(false);
		clearData();
		if (type === 'room') {
			editGroup.room_id = +id;
		}
		showAddEditGroupModal = true;
	};

	const checkForMissingDevice = (devicesArr) => {
		const missingIds = devicesArr.filter(
			(id) => !Object.values($devices).some((device) => device.id === id),
		);

		return missingIds;
	};
</script>

{#if $showNewLayout && type === 'room'}
	<RoomToolbarWrapper showBulkActions={false} {id} {list} itemsLength={items.length} />
{:else if $showNewLayout}
	<GroupsToolbar disableReorder={items.length <= 1} {type} {id} />
{:else}
	<Toolbar class="mb-m" isSticky>
		<svelte:fragment slot="breadcrumbs">
			{#if type === 'room'}
				<RoomBreadCrumbs {id} />
			{:else}
				<Breadcrumbs
					aria-label={$format('label_groups', { default: 'Groups' })}
					links={[{ label: capFirstChar($format('tab_groups')) }]}
				/>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="leftSide">
			{#if type === 'room'}
				<RoomToolbar />
			{/if}

			{#if type === 'groups'}
				<div class="d-flex align-items-center justify-content-center">
					<Input
						class="width-md-m mr-sm-s text-capitalize-placeholder"
						bind:value={$groupMainFilter}
						type="text"
						label={capFirstChar($format('search', { default: 'Search' }))}
						icon="fas fa-search"
						id="groups-toolbar-search-input"
						aria-labelledby="groups-toolbar-search-input"
					/>
					<IconButton
						icon="fa-solid fa-filter"
						on:click={() => {
							openFilterModal = true;
						}}
						aria-label={$format('aria_open_filter', { default: 'Open filter' })}
						{notification}
					/>
				</div>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="rightSide">
			<AddEditButtons
				disableEditButton={items.length <= 0}
				disableAddButton={isEmptyDeviceList}
				interceptEditClick={isActiveHomeShared($homes.list)}
				on:add_click={() => handle_add_group()}
				on:edit_click={() => {
					sharedManagedContext = 'edit_entity';
					showSharedManagedModal = true;
				}}
				hideAddButton={$showNewLayout}
			/>
		</svelte:fragment>
	</Toolbar>
{/if}

<!-- {#if isEmptyDeviceList && Object.values($groups).length <= 0}
	<EmptyDeviceList />
	{/if} -->
{#if items.length <= 0 && !!$groupMainFilter}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyList
			icon="fal fa-magnifying-glass"
			title={$format('no_such_group_found', { default: 'No such groups found.' })}
			description={$format('check_the_spelling_or_try_new_search', {
				default: 'Check the spelling or try a new search.',
			})}
		/>
	</ListEmptyStateSurface>
{:else if items.length <= 0 && isActiveHomeShared($homes.list)}
	{@const sharedEmptyCopy = getSharedHomeEmptyListCopy('groups')}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyList
			icon="fal fa-layer-group"
			title={$format(sharedEmptyCopy.titleKey, { default: sharedEmptyCopy.titleDefault })}
			description={$format(sharedEmptyCopy.descrKey, {
				default: sharedEmptyCopy.descrDefault,
			})}
		/>
	</ListEmptyStateSurface>
{:else if items.length <= 0}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyList
			icon="fal fa-layer-group"
			title={$format('no_groups_title', { default: 'There are no groups yet' })}
			description={$format('no_groups_descr', {
				default: 'Try creating a group of your devices for group control for your convenience.',
			})}
			btnText={$format('button_add_group', { default: 'Add group' })}
			onClick={() => handle_add_group()}
		/>
	</ListEmptyStateSurface>
{:else if $forceListSkeleton}
	<ListSkeletonGrid class={$edit && $showNewLayout ? 'mt-s' : ''} variant="list-menu" />
{:else}
	<CardGrid
		class={$edit && $showNewLayout ? 'mt-s' : ''}
		editMode={$edit}
		xl={350}
		lg={290}
		sm={270}
		{dndSettings}
		on:consider={handleReorder}
		on:finalize={handleReorderEnd}
	>
		{#each items as item, index (item)}
			{@const missignDevices = checkForMissingDevice(item.devices)}
			{@const hasMissingDevice = !!missignDevices.length}
			<div
				data-dnd-item
				class:dnd-placeholder={item.isDndShadowItem}
				tabindex="0"
				role="button"
				aria-label={$format('button_drag_group_to_reorder', {
					default: 'Drag group to reorder',
				})}
			>
				<ReorderStepControls
					variant="vertical"
					{index}
					listLength={items.length}
					enabled={$edit && items.length > 1}
					on:step={handleStepReorder}
				>
					<GroupsListContent
						{item}
						{load_image_from}
						{onCardClick}
						{onCardDelete}
						{onCardEdit}
						{hasMissingDevice}
						on:handle-drag-start={(e) => onStartDrag(e, $edit, handleStartDrag, true)}
						on:handle-drag-end={(e) => onEndDrag(e, $edit, handleEndDrag, true)}
					/>
				</ReorderStepControls>
			</div>
		{/each}
	</CardGrid>
{/if}

<!-- Edit Group Modal -->
{#if showAddEditGroupModal}
	<AddEditGroupWizard {editGroup} bind:showAddEditGroupModal on:finish={onFinishCardEdit} />
{/if}

<!-- Delete Group Modal -->
<GroupDeleteModal bind:showDeleteGroupModal bind:items {editGroup} />

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context={sharedManagedContext} />

<!-- Filter Modal -->
{#if openFilterModal}
	<GroupsFilterModal bind:openFilterModal />
{/if}
